/**
 * Renders a UI suited for adding users to and removing users from some set.
 * ## Events
 * Name | Arguments | Description
 * add | The user model. | Fires whenever a user is added to the set.
 * remove | The user model. | Fires whenever a user is removed from the set.
 * @module app/views/admin/users/AddRemove
 */
import Backbone from "backbone";
import _ from "underscore";
import Field from "core/models/Field";
import CollectionTableView from "core/views/CollectionTableView";
import TabView from "core/views/TabView";
import User from "app/models/User";
export default Backbone.View.extend({
  className: "admin-user-add-remove",

  /**
   * Initializes the view.
   * @param  {object} options The options hash.
   * @param {UserCollection} options.users The entire list of users (included or excluded).
   * @param {Function} options.decider Function which can be run to determine if a user is "included" or not. Given a `User` model, expected to return `true` if the user is included, otherwise `false`.
   * @param {string} options.inTabName:'Included' The label for the interface component containing the users that are included in the set.
   * @param {string} options.outTabName:'All Users' The label for the interface componenet containing the users that are excluded from the set.
   * @param {Array} options.fields A set of `Field` objects to use when displaying the two lists of users.
   */
  initialize: function (options) {
    this.options = _.defaults({}, options, {
      users: [], //the entire list of users (Backbone Collection)
      decider: function () {
        return false;
      }, //a function which can be run to determine if a user is "included"
      inTabName: "Included", //name for tab displaying included users
      outTabName: "All Users", //name for tab displaying non included users
      fields: [], //a set of Field objects to include in each user table
    });

    var UserList = Backbone.Collection.extend({
      model: User,
    });

    this._allUsers = this.options.users;
    this._inUsers = new UserList();
    this._outUsers = new UserList();
    this._initializeUsers();
  },

  _initializeUsers: function () {
    _.each(
      this._allUsers.models,
      function (model) {
        if (this.options.decider(model)) {
          this._inUsers.add(model);
        } else {
          this._outUsers.add(model);
        }
      },
      this
    );
  },

  _includeUser: function (user) {
    this._inUsers.add(user);
    this._outUsers.remove(user);
    this.trigger("add", user);
  },

  _excludeUser: function (user) {
    this._inUsers.remove(user);
    this._outUsers.add(user);
    this.trigger("remove", user);
  },

  _createUserView: function (users, included) {
    var self = this;

    var actionField = new Field({
      id: "action",
      name: "action",
      label: "Action",
      editor: "custom",
      editable: false,
      ViewerView: {
        tagName: "button",
        attributes: {
          type: "button",
        },
        className: "btn btn-default",
        template: _.template('<%-value ? "Remove" : "Add"%>'),

        events: {
          click: function () {
            this.setValue(!this.getControlValue());

            if (this.getControlValue()) {
              self._includeUser(this.options.model);
            } else {
              self._excludeUser(this.options.model);
            }
          },
        },

        getControlValue: function () {
          return this.$el.html() == "Remove";
        },

        setControlValue: function () {
          this.render();
        },
      },
      viewOptions: {
        value: included,
      },
    });

    var fields = this.options.fields.slice();

    fields.unshift(actionField);

    var view = new CollectionTableView({
      collection: users,
      fields: fields,
      allowSave: false,
      allowDelete: false,
      mode: "view",
    });

    view.render();

    return view;
  },

  render: function () {
    var tabView = new TabView({
      tabs: [
        {
          title: this.options.inTabName,
          view: this._createUserView(this._inUsers, true),
        },
        {
          title: this.options.outTabName,
          view: this._createUserView(this._outUsers, false),
        },
      ],
    });

    tabView.on("enter", function (tab) {
      tab.view.$el.css("display", "table");
    });
    this.$el.append(tabView.$el);
    tabView.render();
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/admin/users/AddRemove.js
// module id = 500
// module chunks = 1